import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { PageHeader } from '../components/PageHeader';
import { errorText, useApi } from '../lib/api';
import { fmt, playtestMetrics, type SkillMetrics } from '../lib/skillsApi';

const th = 'px-2 py-1 text-left';
const td = 'px-2 py-1';
const card = 'rounded border-2 border-ink bg-white p-4';

function Rows({ rows }: { rows: [string, string][] }) {
  return (
    <table className="text-sm">
      <tbody>
        {rows.map(([k, v]) => (
          <tr key={k} className="border-t border-newsprint">
            <td className={`${td} text-bruise`}>{k}</td>
            <td className={`${td} font-mono`}>{v}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Outta-Ten (`skills`, SkillMetricsView): median days to 5 / 7 / 10 per stat or skill, skills over 7 per player, rust events a
 * week, body stats by health level and points by source. Null until a run has a sheet (or on an older API). */
function SkillsBlock({ s }: { s: SkillMetrics | null | undefined }) {
  if (s == null)
    return (
      <p className="text-sm text-bruise" data-testid="skills-absent">
        No Outta-Ten measures in this window yet.
      </p>
    );
  const sources = Object.entries(s.pointsBySource ?? {});
  return (
    <>
      <Rows
        rows={[
          ['Skills over 7 per player', fmt(s.skillsOver7PerPlayer)],
          ['Rust events per week', fmt(s.rustEventsPerWeek)],
          ...sources.map(([k, v]): [string, string] => [`Points from ${k}`, fmt(v)]),
        ]}
      />
      <h3 className="mt-3 text-sm font-semibold">Median days to a level</h3>
      {(s.byId ?? []).length === 0 ? (
        <p className="text-sm">Nobody has trained yet.</p>
      ) : (
        <table className="mt-1 text-xs" data-testid="median-days">
          <thead className="text-bruise">
            <tr>
              <th className={th}>Stat or skill</th>
              <th className={th}>Players</th>
              <th className={th}>To 5</th>
              <th className={th}>To 7</th>
              <th className={th}>To 10</th>
            </tr>
          </thead>
          <tbody>
            {s.byId.map((r) => (
              <tr key={`${r.kind}:${r.id}`} className="border-t border-newsprint">
                <td className={`${td} font-mono`}>{`${r.kind}:${r.id}`}</td>
                <td className={`${td} font-mono`}>{fmt(r.players)}</td>
                <td className={`${td} font-mono`}>{fmt(r.medianDaysTo5)}</td>
                <td className={`${td} font-mono`}>{fmt(r.medianDaysTo7)}</td>
                <td className={`${td} font-mono`}>{fmt(r.medianDaysTo10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <h3 className="mt-3 text-sm font-semibold">Body stats by health level</h3>
      {(s.bodyByHealthLevel ?? []).length === 0 ? (
        <p className="text-sm">No players with a health level yet.</p>
      ) : (
        <table className="mt-1 text-xs" data-testid="body-by-level">
          <thead className="text-bruise">
            <tr>
              <th className={th}>Health level</th>
              <th className={th}>Players</th>
              <th className={th}>Median END</th>
              <th className={th}>Median STR</th>
            </tr>
          </thead>
          <tbody>
            {s.bodyByHealthLevel.map((r) => (
              <tr key={r.level} className="border-t border-newsprint">
                <td className={`${td} font-mono`}>{r.level}</td>
                <td className={`${td} font-mono`}>{fmt(r.players)}</td>
                <td className={`${td} font-mono`}>{fmt(r.medianEnd)}</td>
                <td className={`${td} font-mono`}>{fmt(r.medianStr)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

/**
 * Playtest metrics (`GET /admin/playtest/metrics?days=N`, Staff, read-only): the homebase measures (milestones, marks a day, debuff
 * share, bed stays, home flips) and the Outta-Ten block (ADR 0066 §7). Any block the API leaves out says so instead of failing.
 * What each number means and which knob moves it: docs/game/balance-knobs.md.
 */
export function PlaytestMetricsPage() {
  const { client } = useApi();
  const [days, setDays] = useState(14);
  const q = useQuery({
    queryKey: ['admin-playtest-metrics', days],
    queryFn: () => playtestMetrics(client, days),
  });
  const m = q.data;
  return (
    <>
      <PageHeader
        title="Playtest metrics"
        description="The playtest measures over the last N days: stat milestones, marks a day, debuffs, bed stays, home flips, and the Outta-Ten skill pace, rust and body by health level."
      >
        <label className="text-sm font-semibold">
          Days{' '}
          <input
            aria-label="Days"
            type="number"
            min={1}
            max={90}
            className="w-20 rounded border-2 border-ink px-2 py-0.5"
            value={days}
            onChange={(e) => setDays(Math.min(90, Math.max(1, Number(e.target.value) || 1)))}
          />
        </label>
      </PageHeader>
      {q.isPending && <p className="text-sm">Loading…</p>}
      {q.error && <p className="text-sm text-blam">{errorText(q.error)}</p>}
      {m && (
        <div className="grid gap-4 lg:grid-cols-2">
          <section className={card}>
            <h2 className="font-semibold">Window</h2>
            <Rows
              rows={[
                ['From', m.from ?? '-'],
                ['To', m.to ?? '-'],
                ['Runs', fmt(m.runs)],
              ]}
            />
          </section>
          <section className={card}>
            <h2 className="font-semibold">Stats and skills (Outta-Ten)</h2>
            <SkillsBlock s={m.skills} />
          </section>
          {m.milestones && (
            <section className={card}>
              <h2 className="font-semibold">Stat milestones</h2>
              <Rows
                rows={[
                  ['Players trained', fmt(m.milestones.playersTrained)],
                  ['Reached 7', fmt(m.milestones.reached7)],
                  ['Median days to 7', fmt(m.milestones.medianDaysTo7)],
                  ['Reached 10', fmt(m.milestones.reached10)],
                  ['Median days to 10', fmt(m.milestones.medianDaysTo10)],
                ]}
              />
            </section>
          )}
          {m.debuffs && (
            <section className={card}>
              <h2 className="font-semibold">Debuffs</h2>
              <Rows
                rows={[
                  ['Run hours', fmt(m.debuffs.runHours)],
                  ['Debuff hours', fmt(m.debuffs.debuffHours)],
                  ['Share', fmt(m.debuffs.share, 2)],
                ]}
              />
            </section>
          )}
          {m.beds && (
            <section className={card}>
              <h2 className="font-semibold">Bed stays</h2>
              <Rows
                rows={[
                  ['Admitted', fmt(m.beds.admitted)],
                  ['Closed', fmt(m.beds.closed)],
                  ['Median stay h', fmt(m.beds.medianStayHours)],
                  ['p90 stay h', fmt(m.beds.p90StayHours)],
                  ['Median wait h', fmt(m.beds.medianWaitHours)],
                ]}
              />
            </section>
          )}
          {m.homeFlips && (
            <section className={card}>
              <h2 className="font-semibold">Home flips</h2>
              <Rows
                rows={[
                  ['Flips', fmt(m.homeFlips.flips)],
                  ['Players', fmt(m.homeFlips.players)],
                  ['Per player per 30 days', fmt(m.homeFlips.perPlayerPer30Days, 2)],
                  ['Reversals within 14 days', fmt(m.homeFlips.reversalsWithin14Days)],
                ]}
              />
            </section>
          )}
          {(m.notes ?? []).length > 0 && (
            <section className={`${card} lg:col-span-2`}>
              <h2 className="font-semibold">Notes</h2>
              <ul className="list-disc pl-5 text-sm">
                {m.notes!.map((n) => (
                  <li key={n}>{n}</li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}
    </>
  );
}
